import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';

import { Flex } from '@revenuemarket/cds';
import { Colors, PrimaryColors, typographyStyle } from '@revenuemarket/design';
import { NoEffect, ZeroPercent, Document } from '@revenuemarket/ui';
import { formatMoney } from '@revenuemarket/utils';
import { ButtonProps, Typography } from 'antd';
import dayjs from 'dayjs';
import { useRouter } from 'next/router';
import { FinanceCard, NoTerm, TermOffered, TermRequested } from '~apis/dashboard';
import { CompanyUserPermission } from '~apis/role/type';
import RequestTermButton from '~components/FinanceInfo/buttons/RequestTermButton';
import TradeRevenueButton from '~components/FinanceInfo/buttons/TradeRevenueButton';
import ConsultReservationFloatingSection from '~components/FinanceInfo/ConsultReservationFloatingSection';
import TermInfo from '~components/FinanceInfo/TermInfo';
import TermSimulation from '~components/FinanceInfo/TermSimulation';
import UsecaseSection from '~components/FinanceInfo/UsecaseSection';
import { PermissionGuard } from '~components/Role/PermissionGuard';
import { getDetailedDeniedDescription } from '~components/TermRequest/utils';
import checkIsOnboardingUser from '~hooks/checkIsOnboardingUser';
import { usePageHeight } from '~hooks/layout/usePageHeight';
import { useGetFinanceInfo } from '~hooks/queries/dashboard/query';
import { useGetSettings } from '~hooks/queries/setting/query';
import { useTradeAllowed } from '~hooks/tax-invoice/useTradeAllowed';
import { useCompanyId } from '~hooks/useCompanyId';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';
import { mixpanel } from '~pages/_app';

const Benenfit = ({
  title,
  description,
  icon,
}: {
  title: string;
  description: string;
  icon: React.ReactNode;
}) => {
  return (
    <Flex direction="row" gap={16} align="center">
      <Flex
        style={{
          width: 36,
          height: 36,
          borderRadius: 8,
          backgroundColor: PrimaryColors.primary50,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {icon}
      </Flex>
      <Flex direction="column" gap={4}>
        <Typography.Text style={{ ...typographyStyle['14_600'], color: Colors.gray800 }}>
          {title}
        </Typography.Text>
        <Typography.Text style={{ ...typographyStyle['14_400'], color: Colors.gray800 }}>
          {description}
        </Typography.Text>
      </Flex>
    </Flex>
  );
};

const Title = ({ financeInfo }: { financeInfo?: FinanceCard }) => {
  if (financeInfo?.type === 'TERM_OFFERED' && financeInfo.finalOffer === 'OFFERED') {
    return (
      <Typography.Text
        style={{ ...typographyStyle['24_600'], color: Colors.gray800, whiteSpace: 'pre-line' }}
      >
        {`최대 ${formatMoney(financeInfo.payoutLimit)}원을 받을 수 있어요.
        지금 바로 장래매출채권 양도를 신청해보세요.`}
      </Typography.Text>
    );
  }
  if (financeInfo?.type === 'TERM_REQUESTED') {
    return (
      <Typography.Text style={{ ...typographyStyle['24_600'], color: Colors.gray800 }}>
        한도를 분석 중입니다.
        <br />
        분석이 완료되면 이메일로 알려드려요
      </Typography.Text>
    );
  }

  if (financeInfo?.type === 'TERM_OFFERED' && financeInfo.denialReason) {
    return (
      <Typography.Text style={{ ...typographyStyle['24_600'], color: Colors.gray800 }}>
        지금은 매출을 판매할 수 없어요
        <br />
        {`${
          getDetailedDeniedDescription(financeInfo.denialReason, financeInfo.payoutLimit)
            ?.description ?? ''
        }`}
      </Typography.Text>
    );
  }
  return (
    <Typography.Text style={{ ...typographyStyle['24_600'], color: Colors.gray800 }}>
      우리 회사 매출로 받을 수 있는 한도를 알아보세요
    </Typography.Text>
  );
};

const FinanceContent = React.memo(
  ({
    financeInfo,
    pageHeight,
    rightGap,
    containerRef,
    isDisabled,
    router,
  }: {
    financeInfo: FinanceCard;
    pageHeight: string | number;
    rightGap: number;
    containerRef: React.RefObject<HTMLDivElement>;
    isDisabled: boolean;
    router: ReturnType<typeof useRouter>;
  }) => {
    const settings = useGetSettings();

    const step = settings.data?.Data?.data?.onboardingStep;
    const isOnboardingUser = checkIsOnboardingUser(step);

    const ActionButton = ({ financeInfo, style }: { financeInfo: FinanceCard } & ButtonProps) => {
      if (!financeInfo) {
        return null;
      }
      if (financeInfo?.type === 'TERM_OFFERED' && financeInfo?.finalOffer === 'OFFERED') {
        return (
          <Flex direction="column" gap={4}>
            <TradeRevenueButton
              buttonType="weak"
              title="장래매출채권 양도 신청하러 가기"
              style={style}
              disabled={isDisabled}
              onClick={() => {
                mixpanel.track(MIXPANEL_EVENT.FINANCE.CLICK_TRADE_REVENUE_APPLY);
                router.push('/dashboard/trade-revenue');
              }}
            />
            {isDisabled && (
              <Typography.Text
                style={{
                  ...typographyStyle['13_400'],
                  color: Colors.gray700,
                }}
              >
                {dayjs(financeInfo?.payoutDate).format('YYYY년 M월 D일')} 일정은 선착순 신청이
                마감됐어요.
              </Typography.Text>
            )}
          </Flex>
        );
      }
      return <RequestTermButton style={style} />;
    };

    const TermContent = React.memo(({ financeInfo }: { financeInfo: FinanceCard }) => {
      if (!financeInfo) {
        return null;
      }

      if (financeInfo?.type === 'TERM_OFFERED' && financeInfo?.finalOffer === 'OFFERED') {
        return (
          <Flex direction="column" gap={4}>
            <TermInfo
              expectedPayout={financeInfo.payoutLimit}
              tradeApplyDate={financeInfo.tradeApplyDate}
              discountRate={financeInfo.tradingDiscountRate}
              repaymentPeriod={financeInfo.tradingPeriod}
              payoutDate={financeInfo.payoutDate}
              repaymentSchedules={financeInfo.repaymentSchedules}
              mainActionTitle="장래매출채권 양도 신청하러 가기"
              onClickMainAction={() => {
                mixpanel.track(MIXPANEL_EVENT.FINANCE.CLICK_TRADE_REVENUE_APPLY);
                router.push('/dashboard/trade-revenue');
              }}
              item={financeInfo}
            />
            {isDisabled && (
              <Typography.Text
                style={{
                  ...typographyStyle['13_400'],
                  color: Colors.gray700,
                }}
              >
                {dayjs(financeInfo?.payoutDate).format('YYYY년 M월 D일')} 일정은 선착순 신청이
                마감됐어요.
              </Typography.Text>
            )}
          </Flex>
        );
      }
      return (
        <>
          <TermSimulation />
          <RequestTermButton />
        </>
      );
    });

    // Responsive direction based on Flex's own width
    const [flexDirection, setFlexDirection] = useState<'row' | 'column'>('row');
    const flexRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
      if (!flexRef.current) return;
      const handleResize = (entries: ResizeObserverEntry[]) => {
        const width = entries[0].contentRect.width;
        setFlexDirection(width <= 980 ? 'column' : 'row');
      };
      const observer = new ResizeObserver(handleResize);
      observer.observe(flexRef.current);
      // Set initial value
      handleResize([
        { contentRect: flexRef.current.getBoundingClientRect() } as ResizeObserverEntry,
      ]);
      return () => observer.disconnect();
    }, []);

    return (
      <Flex
        ref={flexRef}
        direction={flexDirection}
        justify={flexDirection === 'row' ? 'center' : 'flex-start'}
        align={flexDirection === 'column' ? 'center' : 'flex-start'}
        gap={32}
        style={{
          backgroundColor: Colors.white,
          maxHeight: pageHeight,
          overflowY: 'scroll',
          paddingTop: 56,
          paddingBottom: 56,
          position: 'relative',
        }}
      >
        <Flex
          direction="column"
          gap={24}
          style={{
            width: 520,
            ...typographyStyle['24_600'],
            color: Colors.gray800,
          }}
        >
          <Title financeInfo={financeInfo} />
          <Flex direction="column" gap={16}>
            <Flex direction="column" gap={12}>
              <Typography.Text style={{ ...typographyStyle['16_600'], color: Colors.gray800 }}>
                클로브 금융은?
              </Typography.Text>
              <Typography.Text style={{ ...typographyStyle['14_400'], color: Colors.gray800 }}>
                과거 매출 데이터를 토대로 미래 매출을 현금화 해드리는 장래매출채권 유동화
                서비스입니다.
              </Typography.Text>
            </Flex>
            <ActionButton financeInfo={financeInfo} style={{ width: 'fit-content' }} />
          </Flex>
          <Flex direction="row" gap={16}>
            <Flex
              direction="column"
              gap={8}
              style={{
                padding: 24,
                border: `1px solid ${Colors.grayOpacity07}`,
                borderRadius: 16,
                flex: '1 0 0',
                boxShadow: '0px 8px 16px 0px rgba(29, 34, 37, 0.02)',
              }}
            >
              <Typography.Text style={{ ...typographyStyle['13_400'], color: Colors.gray800 }}>
                클로브 금융을 활용한 회사
              </Typography.Text>
              <Typography.Text style={{ ...typographyStyle['20_600'], color: Colors.gray800 }}>
                {`${formatMoney((financeInfo as any)?.numOfCumulativeCustomers ?? 0)}개 회사`}
              </Typography.Text>
            </Flex>
            <Flex
              direction="column"
              gap={8}
              style={{
                padding: 24,
                border: `1px solid ${Colors.grayOpacity07}`,
                borderRadius: 16,
                flex: '1 0 0',
                boxShadow: '0px 8px 16px 0px rgba(29, 34, 37, 0.02)',
              }}
            >
              <Typography.Text style={{ ...typographyStyle['13_400'], color: Colors.gray800 }}>
                지급 받은 총 양도대금
              </Typography.Text>
              <Typography.Text style={{ ...typographyStyle['20_600'], color: Colors.gray800 }}>
                {`${formatMoney((financeInfo as any)?.cumulativePayout ?? 0)}원`}
              </Typography.Text>
            </Flex>
          </Flex>
          <Flex direction="column" gap={16}>
            <Benenfit
              title="서류 없이"
              description="우리 회사 데이터로 한도를 확인해요"
              icon={<Document />}
            />
            <Benenfit
              title="지분 희석 없이"
              description="장래매출채권유동화로 자금을 조달해요"
              icon={<ZeroPercent />}
            />
            <Benenfit
              title="차입 비율 영향 없이"
              description="차입금이 아닌 미지급금으로 분류돼요"
              icon={<NoEffect />}
            />
          </Flex>
          <TermContent financeInfo={financeInfo} />
          {financeInfo?.type === 'TERM_OFFERED' && financeInfo?.finalOffer === 'OFFERED' && (
            <ConsultReservationFloatingSection />
          )}
        </Flex>
        <UsecaseSection containerRef={containerRef} />
      </Flex>
    );
  },
);

const FinancePage = () => {
  const { companyId } = useCompanyId();
  const { data } = useGetFinanceInfo({
    companyId,
  });
  const router = useRouter();
  const isDisabled = !useTradeAllowed(companyId);
  const financeInfo = data?.Data?.data?.items.find(
    (item) =>
      item.type === 'TERM_OFFERED' || item.type === 'NO_TERM' || item.type === 'TERM_REQUESTED',
  ) as NoTerm | TermOffered | TermRequested;

  const containerRef = useRef<HTMLDivElement>(null);
  const [rightGap, setRightGap] = useState(0);

  const pageHeight = usePageHeight();

  useEffect(() => {
    if (!containerRef.current) return;

    const calculateGap = () => {
      if (containerRef.current) {
        const viewportWidth = window.innerWidth;
        const elementRect = containerRef.current.getBoundingClientRect();
        const gap = viewportWidth - (elementRect.x + elementRect.width);
        setRightGap(gap);
      }
    };

    const resizeObserver = new ResizeObserver(calculateGap);
    resizeObserver.observe(containerRef.current);

    window.addEventListener('resize', calculateGap);

    calculateGap();

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', calculateGap);
    };
  }, []);

  return (
    <PermissionGuard showModal required={CompanyUserPermission.CAN_MANAGE_CLOBE_FINANCE}>
      <FinanceContent
        financeInfo={financeInfo}
        pageHeight={pageHeight}
        rightGap={rightGap}
        containerRef={containerRef}
        isDisabled={isDisabled}
        router={router}
      />
    </PermissionGuard>
  );
};

export default FinancePage;
